/* ServiceIntelligenceTab — server leaderboard, table-turn, discount patterns. */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Trophy, Clock, BadgePercent } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function ServiceIntelligenceTab({ outletId }) {
  const [lb, setLb] = useState(null);
  const [tt, setTt] = useState(null);
  const [dv, setDv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [l, t, d] = await Promise.all([
          api.get(`/outlet-intel/${outletId}/service/server-leaderboard?days=7`),
          api.get(`/outlet-intel/${outletId}/service/table-turn?days=7`),
          api.get(`/outlet-intel/${outletId}/service/discounts-voids?days=7`),
        ]);
        if (cancelled) return;
        setLb(l.data); setTt(t.data); setDv(d.data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [outletId]);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading service intelligence…</div>;

  const leaderRev = lb?.rows?.[0]?.revenue || 1;

  return (
    <div className="space-y-4" data-testid="service-intel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Table turn avg" value={`${tt?.overall_avg_turn_min || 0} min`} sub={`${tt?.total_turns || 0} dine-in turns (7d)`} icon={Clock} testid="kpi-turn" />
        <Kpi label="Top server" value={lb?.rows?.[0]?.server || '—'} sub={INR(lb?.rows?.[0]?.revenue) + ' revenue'} icon={Trophy} highlight testid="kpi-top" />
        <Kpi label="Discount share" value={`${dv?.discount_pct_of_orders || 0}%`} sub={`${dv?.orders_with_discount || 0} of ${dv?.total_orders || 0} orders`} icon={BadgePercent} testid="kpi-disc-orders" />
        <Kpi label="Discount ₹ on revenue" value={`${dv?.discount_pct_of_revenue || 0}%`} sub={INR(dv?.total_discount_value) + ' given back'} icon={BadgePercent} testid="kpi-disc-rev" />
      </div>

      {/* Server leaderboard */}
      <Card title="Server Leaderboard (7d)" testid="card-leaderboard">
        <div className="space-y-1.5">
          {(lb?.rows || []).map((r, i) => {
            const pct = Math.max(5, (r.revenue / leaderRev) * 100);
            return (
              <div key={r.server} className="flex items-center gap-3 text-[12px]">
                <span className="w-6 text-right" style={{ color: '#9CA3AF' }}>#{i + 1}</span>
                <span className="w-24 font-medium">{r.server}</span>
                <span className="flex-1 rounded-full h-2" style={{ backgroundColor: '#F3F4F6' }}>
                  <span className="block h-2 rounded-full" style={{
                    width: `${pct}%`, backgroundColor: i === 0 ? '#F59E0B' : '#1B2A4A',
                  }} />
                </span>
                <span className="w-16 text-right text-[10px]" style={{ color: '#6B7280' }}>{r.orders} ord</span>
                <span className="w-14 text-right text-[10px]" style={{ color: '#6B7280' }}>{r.covers} cov</span>
                <span className="w-20 text-right text-[10px]" style={{ color: '#6B7280' }}>avg {INR(r.avg_check)}</span>
                <span className="w-24 text-right font-semibold">{INR(r.revenue)}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Table turn detail */}
      <Card title={`Top Tables by Turns (7d) · overall avg ${tt?.overall_avg_turn_min || 0} min`} testid="card-tables">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(tt?.top_tables || []).slice(0, 12).map(r => (
            <div key={r.table} className="rounded-lg p-3 text-[12px]" style={{ backgroundColor: '#F7F5F2' }}>
              <div className="flex justify-between">
                <span className="font-semibold">{r.table}</span>
                <span style={{ color: '#6B7280' }}>{r.turns} turns</span>
              </div>
              <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>
                avg {r.avg_turn_min} min · party {r.avg_party_size}
              </p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Discounts & Voids (7d)" testid="card-disc">
        <div className="rounded-lg p-3 text-[12px]" style={{ backgroundColor: '#FFFBEB', color: '#92400E' }}>
          <strong>Voids note:</strong> {dv?.voids_note}
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px]">
          <Stat label="Orders with discount" value={`${dv?.orders_with_discount || 0}`} />
          <Stat label="Total discount given" value={INR(dv?.total_discount_value)} />
          <Stat label="Discount % of revenue" value={`${dv?.discount_pct_of_revenue || 0}%`} />
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, icon: Icon, testid }) {
  const bg = highlight ? '#1B2A4A' : 'white';
  const fg = highlight ? 'white' : '#1B2A4A';
  const muted = highlight ? '#9DB5CC' : '#6B7280';
  return (
    <div className="rounded-xl p-4" style={{
      backgroundColor: bg, color: fg, border: highlight ? 'none' : '1px solid #E5E1DC',
    }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} color={muted} />}
        <p className="text-[10px] uppercase tracking-wider" style={{ color: muted }}>{label}</p>
      </div>
      <p className="text-xl font-semibold truncate">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: muted }}>{sub}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg p-2" style={{ backgroundColor: '#F7F5F2' }}>
      <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-base font-semibold" style={{ color: '#1B2A4A' }}>{value}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}
